<template>
  <div>
    <div
      :class="['gallery', orientationComp]"
      @click="setIsGalleryOpen(true)"
    >
      <span class="gallery__guestZone">
        <slot />
      </span>
      <Badge class="gallery__button">
        <p class="gallery__button__content">
          <GroupIcon class="gallery__button__content__icon" />
          <Lang
            by-key="show-all-photos"
            capitalize-first
          />
        </p>
        <p class="gallery__button__counter">
          <span>1 / {{ images.length }}</span>
        </p>
      </Badge>
      <div
        :class="['gallery__main', orientationComp]"
        :style="getImageStyle(mainImage)"
      />
      <div :class="['gallery__secondary', orientationComp]">
        <p
          v-for="(image, index) in secondaryImages"
          :key="index"
          :class="['gallery__secondary__cell', orientationComp]"
          :style="getImageStyle(image)"
        />
      </div>
    </div>
    <transition name="fade">
      <OpenGallery
        v-if="isGalleryOpen"
        :images="images"
        @close="setIsGalleryOpen(false)"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import Badge from '@/core/components/shared/Badge.vue'
import OpenGallery from './OpenGallery.vue'

import GroupIcon from '@/core/components/shared/icons/Group.icon.vue'

import type { Picture } from '~/core/ts/entity/Picture'

export default defineNuxtComponent({
  components: {
    Badge,
    OpenGallery,
    GroupIcon
  },
  props: {
    images: {
      type: Array as PropType<Array<Picture>>,
      required: true,
    },
    orientation: {
      type: String,
      default: 'portrait',
      validator: (value: string) => ['portrait', 'landscape'].includes(value),
    },
  },
  setup(props) {
    const isGalleryOpen = useState('isGalleryOpen', () => false)
    const setIsGalleryOpen = (value: boolean) => isGalleryOpen.value = value

    const defaultImage = 'https://marketplace-assets.s3.eu-west-3.amazonaws.com/2020/10/45656-pre-barrosapalace-spa4206.jpg'
    const ShowImage = (image?: Picture, defaultImageSrc: string = defaultImage): string => {
      if (!image) {
        return defaultImageSrc
      }

      return image.url
    }

    const secondaryImagesStrategy: Record<string, number> = {
      portrait: 5,
      landscape: 4,
    }

    const mainImage = computed(() => ShowImage(props.images[0]))
    const secondaryImagesAmount = computed(() => secondaryImagesStrategy[props.orientation])
    const secondaryImages = computed(() =>
      props.images
        .filter((_, index) => index > 0 && index < secondaryImagesAmount.value)
        .map((file: Picture) => ShowImage(file))
    )

    const getImageStyle = (image: string): string => `background-image: url(${image})`

    const orientationComp = computed(() => {
      if (props.images.length <= 1) {
        return 'landscape'
      }

      return props.orientation
    })

    return {
      mainImage,
      secondaryImages,
      getImageStyle,
      isGalleryOpen,
      setIsGalleryOpen,
      ShowImage,

      orientationComp,
    }
  },
})
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  @apply opacity-100;
  @apply transition-all duration-500;
}
.fade-enter,
.fade-leave-to {
  @apply opacity-0;
  @apply mt-4;
}

.gallery {
  @apply relative;
  @apply cursor-pointer;
  @apply w-full;
  &.portrait {
    @apply grid;
    @apply grid-cols-1;
    @apply gap-2;
    @screen lg {
      @apply grid-cols-2;
    }
  }

  &.landscape {
    @apply grid;
    // @apply grid-rows-2;
    grid-template-rows: 65% 35%;
    @apply gap-2;
  }

  &__main {
    background-repeat: no-repeat;
    background-size: cover;

    @apply rounded-4xl;

    height: 255px;
    @apply w-full;
    &.portrait {
      @screen lg {
        height: 450px;
        margin-right: 5px;

        @apply rounded-none;
        @apply rounded-lg;
      }
    }

    &.landscape {
      @screen lg {
        height: 420px;
        @apply rounded-none;
        @apply rounded-lg;
      }
    }
  }

  &__secondary {
    @apply hidden;

    @screen lg {
      &.portrait {
        height: 450px;
        @apply grid grid-rows-2 grid-cols-2;
        @apply gap-2;
      }
      &.landscape {
        @apply grid;
        @apply grid grid-cols-3;
        @apply gap-2;
      }

      &__cell {
        &.portrait {
          height: 222px;
          &:nth-child(2) {
            @apply rounded-lg;
          }

          &:nth-child(4) {
            @apply rounded-lg;
          }
        }

        &.landscape {
          height: 222px;

          &:nth-child(1) {
            @apply rounded-lg;
          }

          &:nth-child(3) {
            @apply rounded-lg;
          }
        }

        background-repeat: no-repeat;
        background-size: cover;
      }
    }
  }
  &__guestZone {
    @apply absolute;
    @apply left-4;
    @apply top-3;
  }
  &__button {
    @apply absolute;
    right: 20px;
    bottom: 15px;

    @screen lg {
      top: 25px;
      right: 25px;
      @apply bottom-auto;
    }

    &__counter {
      @screen md {
        @apply hidden;
      }
    }

    &__content {
      @apply hidden;
      @apply items-center;
      @screen lg {
        @apply flex;
      }
      &__icon {
        @apply mr-2;
      }
    }
  }
}
</style>
